import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";

import PageHeader from "../../components/shared/PageHeader";
import BlueButton from "../../components/shared/BlueButton";
import LoadingModal from "../../components/shared/LoadingModal";

import { createClient, getClient, updateClient } from "../../api/client";
import { getFileUrl } from "../../api/file";
import ClientForm from "./ClientForm";

export default function ClientFormView() {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formInstance, setFormInstance] = useState(null);
  const [client, setClient] = useState(null);

  const { clientId } = useParams();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const input = await formInstance?.validateFields();
      const address = {
        name: input.addressName,
        street: input.street,
        state: input.state,
        country: input.country,
        addressLine1: input.addressLine1,
        addressLine2: input.addressLine2,
        googlePlaceId: input.googlePlaceId,
        lat: input.lat,
        long: input.long,
      };

      const avatar =
        input.avatar && input.avatar.length > 0
          ? input.avatar.map((file) => {
              if (!file.response) return file;
              return file.response.file;
            })[0]
          : client && client.avatar;

      const data = {
        name: input.name,
        tradingName: input.tradingName,
        phone: input.phone,
        email: input.email,
        billingEmail: input.billingEmail,
        keyContactFirstName: input.keyContactFirstName,
        keyContactLastName: input.keyContactLastName,
        keyContactEmail: input.keyContactEmail,
        address,
        description: input.description,
        avatar,
        attachments: input.attachments,
        contacts: input.contacts,
      };

      if (editing) {
        await updateClient({ ...data, id: clientId }).then(() => {
          message.success("Client updated successfully");
        });
      } else {
        await createClient({ ...data }).then(() => {
          message.success("Client created successfully");
        });
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
    navigate("/clients");
  };

  const fetchClient = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getClient(clientId);
      const activeAttachments =
        res.attachments && res.attachments.filter((att) => att.isActive);
      setClient(res);
      const clientData = {
        ...res,
        avatar: res.avatar && [
          {
            ...res.avatar,
            uid: res.avatar.id,
            name: res.avatar.name,
            status: "done",
            url: getFileUrl(res.avatar),
          },
        ],
        attachments:
          activeAttachments &&
          activeAttachments.map((attachment) => {
            return {
              ...attachment,
              file: {
                ...attachment.file,
                uid: attachment.id,
                status: "done",
                url: getFileUrl(attachment),
              },
            };
          }),
        addressName: res.address && res.address.name,
        street: res.address && res.address.street,
        state: res.address && res.address.state,
        country: res.address && res.address.country,
        addressLine1: res.address && res.address.addressLine1,
        addressLine2: res.address && res.address.addressLine2,
        googlePlaceId: res.address && res.address.googlePlaceId,
        lat: res.address && res.address.lat,
        long: res.address && res.address.long,
      };
      formInstance?.setFieldsValue(clientData);
      if (res.attachments && res.attachments.length > 0) {
        updateDocuments(res);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    }
    setLoading(false);
  }, [formInstance, clientId]);

  const updateDocuments = (client) => {
    const docs = [];
    const activeDocs =
      client.attachments && client.attachments.filter((att) => att.isActive);
    activeDocs.forEach((doc, index) => {
      docs.push(
        {
          key: `${doc.id + index + 1}`,
          label: "Document Name",
          children: doc.name,
        },
        {
          key: `${doc.id + index + 3}`,
          label: "Document",
          children: doc.path,
        }
      );
    });
  };

  useEffect(() => {
    if (clientId) {
      setEditing(true);
      fetchClient({ clientId });
    }
  }, [clientId, fetchClient]);

  return (
    <main className="space-y-6 min-h-full flex flex-col">
      <PageHeader
        title={editing ? "Update Client" : "Create Client"}
        home="Clients"
        route="/clients"
      />

      <div className="bg-white ml-6 p-6 relative">
        <div>
          <ClientForm
            onFormInstanceReady={(instance) => setFormInstance(instance)}
          />
        </div>

        <BlueButton type="primary" onClick={handleSubmit}>
          {editing ? "Update" : "Create"} Client
        </BlueButton>
      </div>

      {loading && <LoadingModal />}
    </main>
  );
}
